import generateMaze, { Cell } from "maze-generation";

const boardWidth = 100;
const boardHeight = 100;

const straightMove = 1;
//const diagonalMove = 1;

/**
 * The ratio between one canvas pixel and one game pixel.
 */
const pixelRatio = 8;

interface Pos {
    x: number;
    y: number;
}

interface ANode extends Pos {
    /**
     * The distance from the start node. (the cost of the path to this node)
     */
    distance: number;
    /**
     * The heuristic distance to the end node. (the estimated cost of the path from this node to the end node)
     */
    heuristic: number;
    from: Pos | null;
}

/**
 * A class representing an A* route.
 */
class ARoute {
    start: ANode;
    end: ANode;
    /**
     * The nodes that have been visited, but not yet finished. Ordered by distance + heuristic.
     */
    stack: ANode[] = [];
    finished: ANode[] = [];

    /**
     * The walls that are in the way.
     */
    walls: Cell[][];

    done = false;
    path = new Array<Pos>();

    steps = 0;
    time = 0;

    /**
     * A function that calculates the direct path distance between two nodes. (returns undefined if no direct path exists)
     * @param a The starting node.
     * @param b The ending node.
     */
    pathTo(a: Pos, b: Pos) {
        // we have a simple grid of pixels, the allowed directions are up, down, left, and right
        // so the (Manhattan) distance is always 1, if they're not adjacent, then there is no direct path
        if (Math.abs(a.x - b.x) + Math.abs(a.y - b.y) === 1) {
            return straightMove;
        }
        return undefined;
    }

    /**
     * A function that calculates the heuristic distance between two positions.
     * @param a The starting position.
     * @param b The ending position.
     */
    heuristic(a: Pos, b: Pos) {
        // use the manhattan distance with a weight
        return 2 * Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
    }

    /**
     * A function that gets the neighbors of a node.
     * @param node The node to get the neighbors of.
     * @returns A list of the neighbor positions.
     */
    neighbors(node: Pos) {
        // get the neighbors of the node (up, down, left, right)
        // make sure they're not out of bounds
        // if the neighbor is a wall, skip it
        const neighbors = new Array<Pos>();
        const cell = this.walls[node.y][node.x];

        if (node.x > 0 && !cell.left) {
            neighbors.push({ x: node.x - 1, y: node.y });
        }
        if (node.x < boardWidth - 1 && !cell.right) {
            neighbors.push({ x: node.x + 1, y: node.y });
        }
        if (node.y > 0 && !cell.up) {
            neighbors.push({ x: node.x, y: node.y - 1 });
        }
        if (node.y < boardHeight - 1 && !cell.down) {
            neighbors.push({ x: node.x, y: node.y + 1 });
        }

        // filter the neighbor we came from and the neighbors in finished
        return neighbors.filter((neighbor) => {
            return !this.finished.some((node) => node.x === neighbor.x && node.y === neighbor.y);
        });
    }

    step() {
        const start = performance.now();
        this.steps++;
        if (this.stack.length === 0) {
            this.done = true;
            throw new Error("No path found.");
        }

        // get the first element of the stack (it'll have the lowest distance + heuristic)
        const current = this.stack.shift()!;

        // add it to finished
        this.finished.push(current);

        // if it's the end, we're done
        if (current.x === this.end.x && current.y === this.end.y) {
            this.#updatePath(current);
            this.done = true;

            const end = performance.now();
            this.time += end - start;

            return;
        }

        // get the neighbors of the current node
        const neighbors = this.neighbors(current);

        // for each neighbor
        for (const neighbor of neighbors) {
            // calculate the distance from the start node
            const distance = current.distance + this.pathTo(current, neighbor)!;

            // calculate the heuristic distance to the end node
            const heuristic = this.heuristic(neighbor, this.end);

            // if the neighbor is already in the stack
            const existing = this.stack.find((node) => {
                return node.x === neighbor.x && node.y === neighbor.y;
            });

            // first option: the node is already in the stack and it has a worse total weight
            if (existing != null && existing.distance + existing.heuristic > distance + heuristic) {
                const node: ANode = {
                    x: existing.x,
                    y: existing.y,
                    distance,
                    heuristic,
                    from: { x: current.x, y: current.y }
                };

                // remove existing, insert node
                this.stack.splice(this.stack.indexOf(existing), 1);
                this.insertIntoStack(node);
                continue;
            }

            // second option: the node is in the stack but it has a better total weight
            if (existing != null) {
                continue;
            }

            // third option: this is a new node, add it to the stack
            this.insertIntoStack({
                x: neighbor.x,
                y: neighbor.y,
                distance,
                heuristic,
                from: { x: current.x, y: current.y }
            });
        }

        const end = performance.now();
        this.time += end - start;
    }

    #updatePath(current: ANode) {
        // reconstruct the path
        const path = new Array<Pos>({ x: current.x, y: current.y });

        // each node has a from property, which is the coordinate of the node it came from
        // all the previous nodes are in finished, so we can just go through the from property
        let node = current;
        while (node.from != null) {
            path.push(node.from);
            node = this.finished.find((node2) => node2.x === node.from!.x && node2.y === node.from!.y)!;
        }

        this.path = path.toReversed();
    }

    /**
     * Insert a node into the stack while keeping the stack sorted by distance + heuristic.
     * @param node The node to insert into the stack.
     */
    insertIntoStack(node: ANode) {
        let insertIndex = 0;

        while (
            insertIndex < this.stack.length &&
            this.stack[insertIndex].distance + this.stack[insertIndex].heuristic < node.distance + node.heuristic
        ) {
            insertIndex++;
        }

        this.stack.splice(insertIndex, 0, node);
    }

    generateMaze() {
        const maze = generateMaze({ width: boardWidth, height: boardHeight, algorithm: "HUNTANDKILL" });

        // Step 1: Generate random start and end points
        const start = ARoute.generateRandomPosition();
        let end = ARoute.generateRandomPosition();

        // Ensure end point is not the same as the start point
        while (ARoute.arePositionsEqual(start, end)) {
            end = ARoute.generateRandomPosition();
        }

        // Step 2: Return the result
        return { maze, startPos: start, endPos: end };
    }

    static generateRandomPosition(): Pos {
        const x = Math.floor(Math.random() * boardWidth);
        const y = Math.floor(Math.random() * boardHeight);
        return { x, y };
    }

    static arePositionsEqual(a: Pos, b: Pos): boolean {
        return a.x === b.x && a.y === b.y;
    }

    constructor() {
        const { startPos, endPos, maze } = this.generateMaze();
        this.walls = maze.toJSON().rows;

        // initialize the route by setting up the first node
        this.start = {
            ...startPos,
            distance: 0,
            heuristic: this.heuristic(startPos, endPos),
            from: null
        };
        this.end = {
            ...endPos,
            distance: Infinity,
            heuristic: 0,
            from: null
        };
        this.stack.push(this.start);
    }
}

const canvas = document.getElementById("canvas") as HTMLCanvasElement;
const ctx = canvas.getContext("2d")!;
canvas.width = boardWidth * pixelRatio;
canvas.height = boardHeight * pixelRatio;

const route = new ARoute();

let running = false;

function update() {
    for (let i = 0; i < 30; i++) {
        if (route.done) {
            running = false;
            (document.getElementById("result") as HTMLParagraphElement)!.innerText = `Steps: ${route.steps}. Time taken: ${route.time}ms.`;
            break;
        }

        try {
            route.step();
        } catch {
            alert("No path found.");

            running = false;
            break;
        }
    }
}

function draw() {
    if (running) {
        update();
    }

    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // draw the stack with a dark orange color
    ctx.fillStyle = "#FFA500";
    for (const node of route.stack) {
        // skip the start node
        if (node.x === route.start.x && node.y === route.start.y) {
            continue;
        }
        ctx.fillRect(node.x * pixelRatio, node.y * pixelRatio, pixelRatio, pixelRatio);
    }

    // draw the finished nodes with a light orange color
    ctx.fillStyle = "#FFD700";
    for (const node of route.finished) {
        // skip the start node
        if (node.x === route.start.x && node.y === route.start.y) {
            continue;
        }
        ctx.fillRect(node.x * pixelRatio, node.y * pixelRatio, pixelRatio, pixelRatio);
    }

    // draw start and end
    ctx.fillStyle = "#f00";
    ctx.fillRect(route.start.x * pixelRatio, route.start.y * pixelRatio, pixelRatio, pixelRatio);
    ctx.fillStyle = "#00f";
    ctx.fillRect(route.end.x * pixelRatio, route.end.y * pixelRatio, pixelRatio, pixelRatio);

    // draw walls with black color
    ctx.fillStyle = "#000";
    for (let x = 0; x < boardWidth; x++) {
        for (let y = 0; y < boardWidth; y++) {
            const cell = route.walls[y][x];
            if (cell.right) {
                // draw a line on the right
                ctx.fillRect((x + 1) * pixelRatio, y * pixelRatio, 1, pixelRatio);
            }
            if (cell.down) {
                // draw a line on the bottom
                ctx.fillRect(x * pixelRatio, (y + 1) * pixelRatio, pixelRatio, 1);
            }
        }
    }

    // draw borders around the canvas
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, canvas.width, 1);
    ctx.fillRect(0, 0, 1, canvas.height);
    ctx.fillRect(canvas.width - 1, 0, 1, canvas.height);
    ctx.fillRect(0, canvas.height - 1, canvas.width, 1);

    if (route.done) {
        // draw the path with a black color
        ctx.strokeStyle = "#000";
        ctx.lineWidth = 1.5;

        // draw it by connecting the dots (which should be in the middle of a grid)
        for (let i = 0; i < route.path.length - 1; i++) {
            ctx.beginPath();
            ctx.moveTo(
                Math.round(route.path[i].x * pixelRatio + pixelRatio / 2),
                Math.round(route.path[i].y * pixelRatio + pixelRatio / 2)
            );
            ctx.lineTo(
                Math.round(route.path[i + 1].x * pixelRatio + pixelRatio / 2),
                Math.round(route.path[i + 1].y * pixelRatio + pixelRatio / 2)
            );
            ctx.stroke();
        }
    }

    requestAnimationFrame(draw);
}

// start when you press enter
document.body.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
        start();
    }

    // if you press S, step once
    if (e.key === "s") {
        route.step();
    }
});

function start() {
    if (running || route.done) return;

    running = true;
    if (instantCheckbox.checked) {
        while (!route.done) {
            route.step();
        }
    }
}

const startButton = document.getElementById("start")! as HTMLButtonElement;
const stepButton = document.getElementById("step")! as HTMLButtonElement;
const instantCheckbox = document.getElementById("instant")! as HTMLInputElement;

startButton.addEventListener("click", start);
stepButton.addEventListener("click", () => {
    route.step();
});

draw();
